import { analyzeDocument, type AnalyzedDocument } from '../analyze';
import { significantTokens } from '../json/lexer';
import { type Token, TokenKind } from '../json/tokens';
import type { Diagnostic } from '../lint/diagnostic';
import { DEFAULT_FORMAT_OPTIONS, type FormatOptions, type Indent } from './options';
import type { FormatResult } from './result';

/**
 * The beautifier: a SINGLE PASS over the token stream that copies every literal through as
 * `source.slice(token.from, token.to)` and decides nothing but the whitespace between
 * tokens (ADR-0013).
 *
 * Two routes were measured and rejected before this one was written, and the reasons are
 * pinned as live assertions in `printer.test.ts` rather than left as folklore:
 *
 * 1. `JSON.parse` + `JSON.stringify` — four milliseconds on 446 KB and *wrong*. It routes
 *    every number through an IEEE-754 double, so `1e10` becomes `10000000000`, `-0` becomes
 *    `0`, `0.1000` becomes `0.1`, `9007199254740993` becomes `…992` and a 20-digit snowflake
 *    id loses its last three digits. It also re-encodes strings: an escape the author chose
 *    to write — a backslash-u sequence, an escaped surrogate pair, an escaped solidus — comes
 *    back unfolded into raw UTF-8. A tool people paste API responses into must not silently
 *    edit their data; "format" is a layout operation.
 * 2. `jsonc.format()` + `applyEdits` — faithful, but `applyEdits` splices the string once per
 *    edit and `format` emits one edit per whitespace run: 5 563 ms for the 93 000 edits of
 *    that same 446 KB document, and 93 seconds at 2 MB. (The pair stays appropriate for the
 *    dozens of edits a repair pass produces — just not for whole-document layout.)
 *
 * This printer measured 14 ms on the same document: linear, one array of chunks, one join.
 *
 * `sortKeys` is deliberately absent in v1 (ADR-0013): reordering members is a semantic
 * transform wearing a formatting costume, and it is not even well defined on a document
 * with duplicate keys.
 */

/** The four punctuators the printer lays out. Everything else is copied verbatim. */
const CLOSER_OF = {
  [TokenKind.OpenBrace]: TokenKind.CloseBrace,
  [TokenKind.OpenBracket]: TokenKind.CloseBracket,
} as const;

const OPENER_OF = {
  [TokenKind.CloseBrace]: TokenKind.OpenBrace,
  [TokenKind.CloseBracket]: TokenKind.OpenBracket,
} as const;

/**
 * Kinds that end a value. Used only by the separator guard below — a well-formed document
 * never puts two of these next to each other without a comma.
 */
function endsAValue(kind: TokenKind): boolean {
  return (
    kind === TokenKind.String ||
    kind === TokenKind.Number ||
    kind === TokenKind.Null ||
    kind === TokenKind.True ||
    kind === TokenKind.False ||
    kind === TokenKind.CloseBrace ||
    kind === TokenKind.CloseBracket ||
    kind === TokenKind.Unknown
  );
}

/** Kinds that begin a value. */
function startsAValue(kind: TokenKind): boolean {
  return (
    kind === TokenKind.String ||
    kind === TokenKind.Number ||
    kind === TokenKind.Null ||
    kind === TokenKind.True ||
    kind === TokenKind.False ||
    kind === TokenKind.OpenBrace ||
    kind === TokenKind.OpenBracket ||
    kind === TokenKind.Unknown
  );
}

/** One level of indentation, as a string. */
function indentUnit(indent: Indent): string {
  return indent.kind === 'tab' ? '\t' : ' '.repeat(indent.width);
}

/**
 * Fill in the defaults field by field rather than by spreading a `Partial`.
 *
 * `exactOptionalPropertyTypes` makes the spread legal, but an explicit `??` per field is
 * also correct for a caller that reaches us from untyped JavaScript with
 * `{ mode: undefined }` — which is exactly what a `postMessage` payload looks like after a
 * structured clone drops a field.
 */
export function resolveFormatOptions(options?: Partial<FormatOptions>): FormatOptions {
  if (options === undefined) return DEFAULT_FORMAT_OPTIONS;
  return {
    mode: options.mode ?? DEFAULT_FORMAT_OPTIONS.mode,
    indent: options.indent ?? DEFAULT_FORMAT_OPTIONS.indent,
    eol: options.eol ?? DEFAULT_FORMAT_OPTIONS.eol,
    finalNewline: options.finalNewline ?? DEFAULT_FORMAT_OPTIONS.finalNewline,
  };
}

/**
 * Reprint `source` from its token stream.
 *
 * Pure, total and linear. It never throws, never inspects a literal's *meaning* and never
 * emits a character that did not come out of `source` — except whitespace, which is the
 * only thing it is allowed to invent.
 *
 * `tokens` may be the full stream (trivia included, as `tokenize` returns it) or an already
 * filtered one; trivia is dropped here either way, so the two are interchangeable. Dropping
 * comments is not an oversight: `FormatOptions` has no `comments` field in v1 because
 * `jsonc/comment` is an `error` and {@link formatAnalyzed} therefore refuses the document
 * before the printer ever sees it. The branch exists so that the printer stays total for
 * callers that reach it directly.
 *
 * Layout for `pretty` (ADR-0013):
 *   `{` `[` — emit, deepen, newline + indent; an empty container collapses to `{}` / `[]`
 *   `}` `]` — undeepen, newline + indent, emit
 *   `,`     — emit, newline + indent
 *   `:`     — emit `": "` (one space after, none before)
 *   else    — emit `source.slice(token.from, token.to)`
 *
 * `minify` is the same walk with every newline and indent suppressed and `:` emitted bare.
 */
export function printTokens(
  source: string,
  tokens: readonly Token[],
  options: FormatOptions,
): string {
  const significant = significantTokens(tokens);
  const pretty = options.mode === 'pretty';
  const eol = options.eol;
  const unit = indentUnit(options.indent);

  /** Indent strings by depth, built once and reused — a 500-deep document allocates 500. */
  const indents: string[] = [''];
  const indentAt = (depth: number): string => {
    const cached = indents[depth];
    if (cached !== undefined) return cached;
    const built = unit.repeat(depth);
    indents[depth] = built;
    return built;
  };

  const out: string[] = [];
  let depth = 0;
  /** The previous token actually emitted. `undefined` before the first one. */
  let previous: Token | undefined;

  /**
   * Keep two values apart when the source kept them apart.
   *
   * `formatAnalyzed` refuses anything carrying an `error`, so in the normal flow two values
   * with no comma between them never reach the printer. But `printTokens` is total, and
   * turning `[1 2]` into `[12]` would be the exact class of silent corruption this whole
   * file exists to prevent.
   *
   * The `previous.to !== token.from` condition is what keeps that guard from causing a
   * corruption of its own. `007` is not one number token — jsonc scans it as `0`, `0`, `7`,
   * because a leading zero is not a legal JSON number — and those three are CONTIGUOUS in
   * the source. Re-emitting them contiguously reproduces the author's bytes exactly;
   * inserting spaces would invent `0 0 7`, text nobody wrote.
   */
  const separateFromPrevious = (token: Token): void => {
    if (previous === undefined) return;
    if (previous.to === token.from) return;
    if (!endsAValue(previous.kind) || !startsAValue(token.kind)) return;
    out.push(pretty ? eol + indentAt(depth) : ' ');
  };

  for (let i = 0; i < significant.length; i += 1) {
    const token = significant[i];
    if (token === undefined) continue;
    // The EOF sentinel is zero width and prints nothing; it exists so that rules never
    // have to special-case the end of the document.
    if (token.kind === TokenKind.EOF) continue;

    switch (token.kind) {
      case TokenKind.OpenBrace:
      case TokenKind.OpenBracket: {
        separateFromPrevious(token);
        out.push(source.slice(token.from, token.to));
        // One token of lookahead is the whole "collapse empty containers" rule.
        const next = significant[i + 1];
        const isEmpty = next !== undefined && next.kind === CLOSER_OF[token.kind];
        if (!isEmpty) {
          depth += 1;
          if (pretty) out.push(eol, indentAt(depth));
        }
        break;
      }

      case TokenKind.CloseBrace:
      case TokenKind.CloseBracket: {
        // Symmetric to the lookahead above: if the opener is what we emitted last, this is
        // the `{}` / `[]` case and no depth was ever taken.
        const isEmpty = previous !== undefined && previous.kind === OPENER_OF[token.kind];
        if (!isEmpty) {
          // Clamped: an unbalanced closer must not drive the indent negative.
          depth = depth > 0 ? depth - 1 : 0;
          // `out.length > 0` keeps a document that opens with a stray `}` from being
          // printed with a blank first line.
          if (pretty && out.length > 0) out.push(eol, indentAt(depth));
        }
        out.push(source.slice(token.from, token.to));
        break;
      }

      case TokenKind.Comma: {
        out.push(',');
        if (pretty) out.push(eol, indentAt(depth));
        break;
      }

      case TokenKind.Colon: {
        out.push(pretty ? ': ' : ':');
        break;
      }

      default: {
        separateFromPrevious(token);
        out.push(source.slice(token.from, token.to));
        break;
      }
    }

    previous = token;
  }

  // An empty (or whitespace-only) document prints as nothing at all. A lone "\n" would be
  // a file the user did not write.
  if (out.length === 0) return '';
  if (pretty && options.finalNewline) out.push(eol);

  return out.join('');
}

/**
 * Format a document whose analysis already exists — the path the UI takes after lint has
 * painted the status pill, so pressing Format costs no second parse.
 *
 * Refuses on any `error`-severity diagnostic and says which ones are blocking, so the
 * caller can offer "Repair, then format" (ADR-0013). The legacy build guessed at a repair
 * and rewrote the user's document silently; that is the behaviour being deleted here.
 */
export function formatAnalyzed(
  doc: AnalyzedDocument,
  options?: Partial<FormatOptions>,
): FormatResult {
  const blocking: Diagnostic[] = doc.report.diagnostics.filter((d) => d.severity === 'error');
  if (blocking.length > 0) {
    return { ok: false, reason: 'has-errors', blocking };
  }

  const text = printTokens(doc.source, doc.tokens, resolveFormatOptions(options));
  return { ok: true, text, changed: text !== doc.source };
}

/**
 * Format a document from nothing but its text (docs/ARCHITECTURE.md §4.5).
 *
 * The convenience entry point, for a caller that has no analysis in hand — a script, a
 * test, the `@jsonfmt/core` consumer who just wants a string. The UI does not use it: it
 * already holds an `AnalyzedDocument` from the lint pass and calls `formatAnalyzed`, so
 * pressing Format costs no second parse.
 *
 * Note what is NOT here: a local "scan for an obvious error and print" check. Validity is
 * decided in exactly one place in this repository, and this function goes there for the
 * answer like everybody else (ADR-0007).
 */
export function format(source: string, options?: Partial<FormatOptions>): FormatResult {
  return formatAnalyzed(analyzeDocument(source), options);
}
